import {
    Card,
    ExpansionPanel,
    ExpansionPanelDetails,
    ExpansionPanelSummary,
    Table,
    TableBody,
    TableCell,
    TableRow,
    Theme,
    Typography
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {makeStyles} from '@material-ui/styles';
import _ from 'lodash';
import {useSnackbar} from 'notistack';
import React from 'react';
import {Can} from '../../../components/Can';
import {LoadingOverlay} from '../../../components/LoadingOverlay';
import TableRowToggle from '../../../components/TableRowToggle/TableRowToggle';
import {getMe, Permission, Role, User} from '../../../graphql/auth';
import getPermissions from '../../../graphql/auth/getPermissions.api';
import userRoleLevel from '../../../utils/userRoleLevel';
import updateUser from '../UserEdit/updateUser.api';
import getRoles from '../../Roles/getRoles.api';

const useStyles = makeStyles((theme: Theme) => ({
    root: {},
    content: {},
    sendButton: {
        marginTop: theme.spacing(2)
    },
    mailIcon: {
        marginRight: theme.spacing(1)
    },
    table: {
        marginTop: theme.spacing(2)
    },
    heading: {
        fontSize: theme.typography.pxToRem(15),
        fontWeight: theme.typography.fontWeightRegular,
    },
}));

const UserRoles = (props: { className?: string, user: User }) => {
    const {user} = props;
    const classes = useStyles();
    const [sendUpdateUser] = updateUser();
    const rolesData = getRoles();
    const myData = getMe();
    const {enqueueSnackbar} = useSnackbar();
    const permissionsData = getPermissions();
    const permissions = permissionsData.loading ? [] : _.orderBy(permissionsData.data!.permissions, 'ident');

    if (myData.loading) return null;

    const myLevel = userRoleLevel(myData.data!.me);
    const userLevel = userRoleLevel(user);
    const canEdit = myLevel <= userLevel;

    const handleChange = (role: Role, active: boolean) => {
        const mutateRoles: { connect?: number[], disconnect?: number[] } = {};
        mutateRoles.connect = !active ? [role.id] : [];
        mutateRoles.disconnect = active ? [role.id] : [];
        const sanitisedData = {
            id: user.id,
            roles: mutateRoles,
        };

        let optimisticRoles = [...user.roles!];

        if (active) {
            optimisticRoles = optimisticRoles.filter(item => item.id !== role.id);
        } else {
            optimisticRoles.push(role);
        }

        sendUpdateUser({
            variables: {data: sanitisedData},
            optimisticResponse: {
                __typename: 'Mutation',
                updateUser: {
                    ...user,
                    __typename: 'User',
                    roles: optimisticRoles,
                },
            }
        })
            .then(() => {
                enqueueSnackbar('User Role Updated.', {variant: 'success'});
            })
            .catch(() => {
                enqueueSnackbar('User Role Update failed.', {variant: 'error'});
            });
    };


    const handlePermissionChange = (permission: Permission, active: boolean) => {
        const mutatePermissions: { connect?: number[], disconnect?: number[] } = {};
        mutatePermissions.connect = !active ? [permission.id] : [];
        mutatePermissions.disconnect = active ? [permission.id] : [];
        const sanitisedData = {
            id: user.id,
            permissions: mutatePermissions,
        };

        let optimisticPermissions = [...user.permissions!];

        if (active) {
            optimisticPermissions = optimisticPermissions.filter(item => item.id !== permission.id);
        } else {
            optimisticPermissions.push(permission);
        }

        sendUpdateUser({
            variables: {data: sanitisedData},
            optimisticResponse: {
                __typename: 'Mutation',
                updateUser: {
                    __typename: 'User',
                    id: user.id,
                    first_name: user.first_name,
                    last_name: user.last_name,
                    email: user.email,
                    permissions: optimisticPermissions,
                },
            }
        });

        // setFormState((oldState: any) => ({
        //     ...oldState,
        //     permissions: optimisticPermissions,
        // }));
    };

    return (
        <LoadingOverlay loading={rolesData.loading}>
            {!rolesData.loading && (
                <Card className={classes.content}>
                    <ExpansionPanel>
                        <ExpansionPanelSummary expandIcon={<ExpandMoreIcon/>} aria-controls="panel1a-content"
                                               id="panel1a-header">
                            <Typography className={classes.heading}>User Roles</Typography>
                        </ExpansionPanelSummary>
                        <ExpansionPanelDetails>
                            <Table className={classes.table}>
                                <TableBody>
                                    {canEdit && rolesData.data && rolesData.data.roles && rolesData.data.roles.map((role) => role.level! >= myLevel && (
                                        <TableRowToggle key={role.ident} name={role.ident!} title={role.ident!}
                                                        onChange={(event) => handleChange(role, event.target.value === 'true')}
                                                        value={user.roles!.some((userRole: Role) => role.id === userRole.id)}/>
                                    ))}
                                    {!canEdit && (
                                        <TableRow>
                                            <TableCell>
                                                <Typography>You do not have a high enough user level to change this
                                                    users roles.</Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </ExpansionPanelDetails>
                    </ExpansionPanel>

                    <ExpansionPanel>
                        <ExpansionPanelSummary expandIcon={<ExpandMoreIcon/>} aria-controls="panel1a-content"
                                               id="panel1a-header">
                            <Typography className={classes.heading}>Extra Permissions</Typography>
                        </ExpansionPanelSummary>
                        <ExpansionPanelDetails>
                            <Table className={classes.table}>
                                <TableBody>
                                    {canEdit && permissions.map((permission) => permission.ident !== '*' && (
                                        <Can key={permission.ident} permission={permission.ident}>
                                            <TableRowToggle name={permission.ident!} title={permission.ident!}
                                                            onChange={(event) => handlePermissionChange(permission, event.target.value === 'true')}
                                                            value={user.permissions!.some(userPermission => permission.id === userPermission.id)}/>
                                        </Can>
                                    ))}
                                    {!canEdit && (
                                        <TableRow>
                                            <TableCell>
                                                <Typography>You do not have a high enough user level to change this
                                                    users permissions.</Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </ExpansionPanelDetails>
                    </ExpansionPanel>

                </Card>
            )}
        </LoadingOverlay>
    );
};

export default UserRoles;
